import pluralize from 'pluralize'

import {
  getStatementStatus,
  hasStatementFailed,
  isStatementNotDuplicated,
} from '@/modules/projects/status'
import type { Statement, StatementStatusKey } from '@/modules/projects/stores'
import { failedSegmentPageCount } from '../tables/statements/coverage'

// Left-to-right order of the progress bar and legend. Segmenting, recognizing and extracting all
// read as "Extracting" here; the wire statuses stay untouched.
export const PAGE_PHASES = [
  'notStarted',
  'enqueued',
  'extracting',
  'categorizing',
  'succeeded',
  'failed',
] as const

export type PagePhase = (typeof PAGE_PHASES)[number]

export type PagePhaseCounts = Record<PagePhase, number>

export interface PageTally {
  statementCount: number
  totalPages: number
  phases: PagePhaseCounts
  statementsByPhase: PagePhaseCounts
}

const PHASE_BY_STATUS: Partial<Record<StatementStatusKey, PagePhase>> = {
  notStarted: 'notStarted',
  enqueued: 'enqueued',
  segmenting: 'extracting',
  recognizing: 'extracting',
  extracting: 'extracting',
  categorizing: 'categorizing',
  succeeded: 'succeeded',
  failed: 'failed',
}

const emptyPhaseCounts = (): PagePhaseCounts => ({
  notStarted: 0,
  enqueued: 0,
  extracting: 0,
  categorizing: 0,
  succeeded: 0,
  failed: 0,
})

const phasePages = (statement: Statement): number =>
  hasStatementFailed(statement)
    ? failedSegmentPageCount(statement)
    : statement.numberOfPages

export const tallyPagesByPhase = (statements: Statement[]): PageTally => {
  const relevant = statements.filter(isStatementNotDuplicated)
  const phases = emptyPhaseCounts()
  const statementsByPhase = emptyPhaseCounts()
  let totalPages = 0

  for (const statement of relevant) {
    totalPages += statement.numberOfPages
    const status = getStatementStatus(statement.status)
    const phase = status ? PHASE_BY_STATUS[status] : undefined
    if (!phase) continue
    phases[phase] += phasePages(statement)
    statementsByPhase[phase] += 1
  }

  return {
    statementCount: relevant.length,
    totalPages,
    phases,
    statementsByPhase,
  }
}

export const describePhase = (tally: PageTally, phase: PagePhase) => {
  const pages = tally.phases[phase]
  const statements = tally.statementsByPhase[phase]
  return `${pages} ${pluralize('page', pages)} across ${statements} ${pluralize('statement', statements)}`
}

export const isEveryPageSucceeded = ({ totalPages, phases }: PageTally) =>
  phases.succeeded === totalPages
